import { FC } from "react";
import { Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";

export type ReceiptNoticeVariant = "individual" | "corporate";

/* ภาพตัวอย่างย่อส่วน + เอกสารคำแนะนำฉบับเต็ม (PDF) ของแต่ละประเภทบัญชี */
const PREVIEW: Record<ReceiptNoticeVariant, { sample: string; guide: string }> = {
  individual: {
    sample: "/files/Bangbow_Individual_Receipt_Sample.jpg",
    guide: "/files/Bangbow_Individual_Receipt_Guide.pdf",
  },
  corporate: {
    sample: "/files/Bangbow_Corporate_Receipt_Sample.jpg",
    guide: "/files/Bangbow_Corporate_Receipt_Guide.pdf",
  },
};

const INDIVIDUAL_TEMPLATE_FILE = "Bangbow_Individual_Receipt_Template.xlsx";

/** ข้อมูลสำคัญที่ต้องมีในใบเสร็จรับเงิน — ตามเอกสารคำแนะนำบุคคลธรรมดา */
const INDIVIDUAL_FIELDS = [
  {
    label: "ข้อมูลผู้รับเงิน",
    detail: "ชื่อ–นามสกุล ที่อยู่ เลขประจำตัวผู้เสียภาษีหรือเลขประจำตัวประชาชน และข้อมูลติดต่อ",
  },
  {
    label: "ข้อมูลผู้จ่ายเงิน",
    detail: "บริษัท แบ่งเบา แพลตฟอร์ม จำกัด พร้อมที่อยู่และเลขประจำตัวผู้เสียภาษี 0105568054745",
  },
  { label: "รายละเอียดเอกสาร", detail: "เลขที่ใบเสร็จรับเงิน วันที่ออกเอกสาร และเลขที่อ้างอิง (ถ้ามี)" },
  { label: "รายละเอียดสินค้าและบริการ", detail: "ชื่อรายการ จำนวน ราคาต่อหน่วย และจำนวนเงินแต่ละรายการ" },
  {
    label: "สรุปยอดเงิน",
    detail: "ยอดรวมสินค้าและบริการ ส่วนลด (ถ้ามี) และจำนวนเงินที่ได้รับชำระจริง โดยไม่มีการคิด VAT",
  },
  { label: "ข้อมูลการรับชำระเงิน", detail: "วันที่รับเงิน ช่องทางการชำระเงิน และลายเซ็นของผู้รับเงิน" },
];

/** ข้อมูลที่ต้องระบุในเอกสารทั้งสองประเภท — ตามเอกสารคำแนะนำนิติบุคคล */
const CORPORATE_FIELDS = [
  "ชื่อ ที่อยู่ และเลขประจำตัวผู้เสียภาษีของผู้ประกอบการ",
  "ข้อมูลผู้ซื้อ: บริษัท แบ่งเบา แพลตฟอร์ม จำกัด เลขประจำตัวผู้เสียภาษี 0105568054745",
  "เลขที่เอกสาร วันที่ออกเอกสาร และรายการสินค้า/บริการ",
  "จำนวนเงินและรายละเอียดการรับชำระเงิน",
  "ลายเซ็นผู้รับเงินหรือผู้มีอำนาจลงนามตามความเหมาะสม",
];

/** นิติบุคคลเลือกแบบฟอร์มตามสถานะการจดทะเบียน VAT */
const CORPORATE_OPTIONS = [
  {
    key: "vat",
    title: "นิติบุคคลที่จดทะเบียน VAT",
    detail:
      "จัดทำใบกำกับภาษี/ใบเสร็จรับเงิน โดยระบุข้อมูลผู้ขายและผู้ซื้อ รายละเอียดสินค้าและบริการ เลขที่เอกสาร วันที่ มูลค่าสินค้าหรือบริการ ภาษีมูลค่าเพิ่ม 7% และยอดรวมตามข้อกำหนดทางภาษี",
    file: "Bangbow_Corporate_VAT_Receipt_Template.xlsx",
    button: "ดาวน์โหลดแบบฟอร์ม (จด VAT)",
  },
  {
    key: "non-vat",
    title: "นิติบุคคลที่ไม่ได้จดทะเบียน VAT",
    detail:
      "จัดทำใบเสร็จรับเงิน โดยระบุข้อมูลผู้รับเงินและผู้ชำระเงิน รายละเอียดสินค้าและบริการ เลขที่ใบเสร็จ วันที่ จำนวนเงิน และยอดรับชำระสุทธิ โดยไม่มีการคิดภาษีมูลค่าเพิ่ม",
    file: "Bangbow_Corporate_NonVAT_Receipt_Template.xlsx",
    button: "ดาวน์โหลดแบบฟอร์ม (ไม่จด VAT)",
  },
];

const IndividualContent: FC = () => (
  <>
    <Title>ใบเสร็จรับเงินสำหรับบุคคลธรรมดา</Title>
    <Desc>
      บริษัท แบ่งเบา แพลตฟอร์ม จำกัด กำหนดให้ผู้ประกอบการที่เป็น<strong>บุคคลธรรมดา</strong>
      จัดทำและแนบใบเสร็จรับเงิน หลังจากได้รับชำระเงินค่าสินค้าหรือบริการเรียบร้อยแล้ว
      เพื่อใช้เป็นหลักฐานยืนยันการรับเงิน ประกอบการตรวจสอบธุรกรรม และการบันทึกบัญชีของบริษัท
    </Desc>

    <SubTitle>ข้อมูลสำคัญที่ต้องมีในใบเสร็จรับเงิน</SubTitle>
    <Steps>
      {INDIVIDUAL_FIELDS.map(({ label, detail }, i) => (
        <li key={label}>
          <StepNo>{i + 1}</StepNo>
          <span>
            <strong>{label}</strong> {detail}
          </span>
        </li>
      ))}
    </Steps>

    <DownloadButton
      type="primary"
      ghost
      icon={<DownloadOutlined />}
      href={`/files/${INDIVIDUAL_TEMPLATE_FILE}`}
      download={INDIVIDUAL_TEMPLATE_FILE}
    >
      ดาวน์โหลดแบบฟอร์ม
    </DownloadButton>
    <Note>
      <span className="required">*</span>ใช้แบบฟอร์มนี้กรณีที่ไม่มีใบเสร็จของท่านเอง (สามารถใช้รูปแบบ Template
      ของท่านเองได้)
    </Note>
  </>
);

const CorporateContent: FC = () => (
  <>
    <Title>ใบเสร็จรับเงิน / ใบกำกับภาษีสำหรับนิติบุคคล</Title>
    <Desc>
      บริษัท แบ่งเบา แพลตฟอร์ม จำกัด กำหนดให้ผู้ประกอบการที่เป็น<strong>นิติบุคคล</strong>
      จัดทำและแนบเอกสารหลักฐานการรับเงิน หลังจากได้รับชำระเงินค่าสินค้าหรือบริการเรียบร้อยแล้ว
      เพื่อใช้ประกอบการตรวจสอบรายการธุรกรรมและการบันทึกบัญชีอย่างถูกต้อง
    </Desc>

    <SubTitle>ข้อมูลที่ต้องระบุในเอกสารทั้งสองประเภท</SubTitle>
    <Bullets>
      {CORPORATE_FIELDS.map((field) => (
        <li key={field}>{field}</li>
      ))}
    </Bullets>

    <SubTitle>เลือกประเภทเอกสารตามสถานะการจดทะเบียน VAT ของท่าน</SubTitle>
    <Options>
      {CORPORATE_OPTIONS.map((option) => (
        <Option key={option.key}>
          <OptionTitle>{option.title}</OptionTitle>
          <OptionDetail>{option.detail}</OptionDetail>
          <DownloadButton
            type="primary"
            ghost
            icon={<DownloadOutlined />}
            href={`/files/${option.file}`}
            download={option.file}
          >
            {option.button}
          </DownloadButton>
        </Option>
      ))}
    </Options>
    <Note>
      <span className="required">*</span>ใช้แบบฟอร์มนี้กรณีที่ไม่มีใบเสร็จขององค์กรท่าน (สามารถใช้รูปแบบ Template
      ขององค์กรท่านได้)
    </Note>
  </>
);

/**
 * คำแนะนำการจัดทำและแนบใบเสร็จรับเงิน แยกตามประเภทบัญชีรับเงิน (บุคคลธรรมดา / นิติบุคคล)
 * ภาพตัวอย่างด้านซ้ายคือแบบฟอร์มจริงย่อส่วน — กดเพื่อเปิดเอกสารคำแนะนำ (PDF) ในแท็บใหม่
 */
const ReceiptTemplateNotice: FC<{ variant: ReceiptNoticeVariant }> = ({ variant }) => {
  const { sample, guide } = PREVIEW[variant];
  return (
    <Wrapper>
      <PaperLink href={guide} target="_blank" rel="noreferrer" aria-label="ดูตัวอย่างใบเสร็จรับเงิน">
        <Paper src={sample} alt="" />
        <PaperCaption>ดูตัวอย่าง</PaperCaption>
      </PaperLink>

      <Body>{variant === "corporate" ? <CorporateContent /> : <IndividualContent />}</Body>
    </Wrapper>
  );
};

export default ReceiptTemplateNotice;

const Wrapper = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #f5f9fd;
  border: 1px solid #d6e6f5;
  border-radius: 10px;
`;

const PaperLink = styled.a`
  flex: none;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  border-radius: 3px;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid #2777c8;
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

/* ภาพตัวอย่างย่อส่วน — ภาพต้นฉบับสัดส่วน 796 × 1066 */
const Paper = styled.img`
  display: block;
  width: 96px;
  height: auto;
  background: #fff;
  border: 1px solid #c9d6e3;
  border-radius: 3px;
  box-shadow: 0 1px 0 #c9d6e3, 0 3px 0 -1px #fff, 0 3px 0 0 #dbe5ee;
`;

const PaperCaption = styled.span`
  font-size: 13px;
  color: #2777c8;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 17px;
  font-weight: 600;
  line-height: 1.4;
  color: #1f2a37;
`;

const Desc = styled.p`
  margin: 6px 0 14px;
  font-size: 15px;
  line-height: 1.6;
  color: #1f2a37;

  strong {
    font-weight: 600;
  }
`;

const SubTitle = styled.div`
  margin-bottom: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2a37;
`;

const Steps = styled.ol`
  margin: 0 0 16px;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 15px;
    line-height: 22px;
    color: #1f2a37;
  }

  strong {
    font-weight: 600;
  }
`;

const Bullets = styled.ul`
  margin: 0 0 16px;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  li {
    font-size: 15px;
    line-height: 22px;
    color: #1f2a37;
  }
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Option = styled.div`
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #d6e6f5;
  border-radius: 8px;
`;

const OptionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: #1f2a37;
`;

const OptionDetail = styled.p`
  margin: 4px 0 10px;
  font-size: 14px;
  line-height: 1.6;
  color: #1f2a37;
`;

const StepNo = styled.span`
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #1f2a37;
  background: rgba(39, 119, 200, 0.12);
`;

/* ปุ่มที่มี href ถูก render เป็น <a> — บังคับให้ข้อความอยู่กึ่งกลางแนวตั้ง (เดิมข้อความชิดขอบบน) */
const DownloadButton = styled(Button)`
  &.ant-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 36px;
    padding: 0 16px;
    line-height: 1;
  }
`;

const Note = styled.div`
  margin-top: 10px;
  font-size: 14px;
  line-height: 1.5;
  color: #1f2a37;

  .required {
    margin-right: 2px;
    color: #e53935;
  }
`;
